import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CheckCircle2,
  XCircle,
  UserPlus,
  Loader2,
} from 'lucide-react';

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { cancelsService } from '@/services/cancels';
import { platformUsersService } from '@/services/platform/users';
import type { CancelRequest } from '@/types/cancels';
import { fmt } from '@/lib/format';
import { toApiError } from '@/lib/errors';
import { CANCEL_STATUS_LABEL, CANCEL_STATUS_TONE } from './CancelRequestsPage';

interface Props {
  cancelRequest: CancelRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CancelRequestDetailSheet({ cancelRequest, open, onOpenChange }: Props) {
  const qc = useQueryClient();
  const [reviewerId, setReviewerId] = useState<string>('');
  const [approveNote, setApproveNote] = useState('');
  const [rejectNote, setRejectNote] = useState('');

  // Re-fetch the fresh row (so we always render the latest status,
  // including newly-arrived portal callbacks) instead of using the
  // snapshot from the list.
  const detailQuery = useQuery({
    queryKey: ['cancels', cancelRequest?.id],
    queryFn: () => cancelsService.get(cancelRequest!.id),
    enabled: open && !!cancelRequest?.id,
    refetchInterval: open ? 10_000 : false,
  });

  const cr = detailQuery.data ?? cancelRequest;

  const usersQuery = useQuery({
    queryKey: ['platform', 'users', 'reviewer-picker'],
    queryFn: () => platformUsersService.list({ pageSize: 200 }),
    enabled: open && cr?.status === 'PENDING_REVIEW',
  });

  useEffect(() => {
    if (!open) {
      setReviewerId('');
      setApproveNote('');
      setRejectNote('');
    }
  }, [open]);

  const onError = (err: unknown) => {
    const e = toApiError(err);
    toast.error(e.message || 'Request failed');
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['cancels'] });
  };

  const assign = useMutation({
    mutationFn: () => cancelsService.assignReviewer(cr!.id, { reviewerUserId: reviewerId }),
    onSuccess: () => {
      invalidate();
      toast.success('Reviewer assigned');
    },
    onError,
  });

  const approve = useMutation({
    mutationFn: () => cancelsService.approve(cr!.id, { reviewerNote: approveNote || undefined }),
    onSuccess: () => {
      invalidate();
      toast.success('Approved · refund initiated to portal');
    },
    onError,
  });

  const reject = useMutation({
    mutationFn: () => cancelsService.reject(cr!.id, { reviewerNote: rejectNote }),
    onSuccess: () => {
      invalidate();
      toast.success('Cancel rejected');
    },
    onError,
  });

  const reviewerOptions = useMemo(
    () => (usersQuery.data?.items ?? []).filter((u) => u.status === 'active'),
    [usersQuery.data],
  );

  if (!cr) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full max-w-2xl overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            <span className="font-mono text-[14px]">{cr.cancelNumber}</span>
            <Badge variant={CANCEL_STATUS_TONE[cr.status]} className="text-[10px]">
              {CANCEL_STATUS_LABEL[cr.status]}
            </Badge>
          </SheetTitle>
          <SheetDescription>
            Customer cancel request against order{' '}
            <span className="font-mono">{cr.salesOrder.orderNumber}</span>
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="overview" className="mt-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="items">Items ({cr.items.length || 'all'})</TabsTrigger>
            <TabsTrigger value="activity">Activity ({cr.statusHistory.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 pt-4">
            <Field label="Customer">
              <div>{cr.customer.name}</div>
              <div className="font-mono text-[11px] text-muted-foreground">{cr.customer.code}</div>
              {cr.customer.email && (
                <div className="text-[11.5px] text-muted-foreground">{cr.customer.email}</div>
              )}
            </Field>
            <Field label="Order">
              <div className="font-mono">{cr.salesOrder.orderNumber}</div>
              <div className="text-[11.5px] text-muted-foreground">
                {cr.salesOrder.status} ·{' '}
                {fmt.money(cr.salesOrder.grandTotal ?? cr.salesOrder.totalAmount, cr.salesOrder.currency)}
              </div>
            </Field>
            <Field label="Requested at">
              <span className="font-mono">{fmt.dateTime(cr.createdAt)}</span>
            </Field>
            {cr.externalRef && (
              <Field label="Portal ref">
                <span className="font-mono text-[11.5px]">{cr.externalRef}</span>
              </Field>
            )}
            {cr.reason && (
              <Field label="Customer reason">
                <span className="whitespace-pre-wrap">{cr.reason}</span>
              </Field>
            )}

            <Separator />

            <Field label="Refund">
              {cr.refundAmount != null ? (
                <div className="space-y-0.5">
                  <div className="font-mono text-[14px]">
                    {fmt.money(cr.refundAmount, cr.refundCurrency ?? 'INR')}
                  </div>
                  {cr.refundId && (
                    <div className="text-[11.5px] text-muted-foreground">
                      Refund ID: <span className="font-mono">{cr.refundId}</span>
                    </div>
                  )}
                  {cr.status === 'REFUND_INITIATED' && (
                    <div className="rounded border border-info/30 bg-info/5 p-2 text-[11.5px]">
                      Waiting on portal to process the gateway refund. Portal will POST back to{' '}
                      <code className="font-mono">/integrations/cancels/{cr.externalRef ?? cr.id}/refund-confirmed</code>.
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-[11.5px] text-muted-foreground">
                  No refund created yet. Approve to auto-create.
                </span>
              )}
            </Field>

            {cr.reviewerNote && (
              <Field label="Reviewer note">
                <span className="whitespace-pre-wrap">{cr.reviewerNote}</span>
              </Field>
            )}

            {cr.status === 'PENDING_REVIEW' && (
              <div className="space-y-3 rounded-lg border border-border/60 bg-muted/10 p-3">
                <div className="text-[12px] font-semibold text-foreground">Review actions</div>

                {!cr.assignedReviewerId ? (
                  <div className="space-y-2">
                    <Select value={reviewerId} onValueChange={setReviewerId}>
                      <SelectTrigger className="h-9 text-[13px]">
                        <SelectValue placeholder="Pick a reviewer …" />
                      </SelectTrigger>
                      <SelectContent>
                        {reviewerOptions.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.fullName ?? u.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={() => assign.mutate()}
                      disabled={!reviewerId || assign.isPending}
                    >
                      {assign.isPending ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <UserPlus className="h-3.5 w-3.5" />
                      )}
                      Assign reviewer
                    </Button>
                  </div>
                ) : (
                  <div className="text-[12px] text-muted-foreground">
                    Currently assigned. Proceed with a decision below.
                  </div>
                )}

                <Separator />

                <div className="space-y-2">
                  <label className="text-[11px] font-medium text-foreground">
                    Approve — reviewer note (optional)
                  </label>
                  <Textarea
                    value={approveNote}
                    onChange={(e) => setApproveNote(e.target.value)}
                    rows={2}
                    placeholder="Customer changed their mind — refunding full amount"
                    className="text-[13px]"
                  />
                  <Button
                    size="sm"
                    onClick={() => approve.mutate()}
                    disabled={approve.isPending}
                  >
                    {approve.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    )}
                    Approve · create refund
                  </Button>
                </div>

                <div className="space-y-2">
                  <label className="text-[11px] font-medium text-foreground">
                    Reject — reviewer note (required)
                  </label>
                  <Textarea
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                    rows={2}
                    placeholder="Order is already shipped — direct customer to return flow"
                    className="text-[13px]"
                  />
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => reject.mutate()}
                    disabled={!rejectNote.trim() || reject.isPending}
                  >
                    {reject.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5" />
                    )}
                    Reject
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="items" className="pt-4">
            {cr.items.length === 0 ? (
              <div className="rounded border border-border/60 bg-muted/20 p-3 text-[12px] text-muted-foreground">
                Full-order cancel — every order line cancels and the refund is the full order
                total.
              </div>
            ) : (
              <table className="w-full text-[12.5px]">
                <thead className="border-b border-border/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-2 py-1.5 text-left">SKU</th>
                    <th className="px-2 py-1.5 text-left">Item</th>
                    <th className="px-2 py-1.5 text-right">Qty</th>
                    <th className="px-2 py-1.5 text-right">Unit</th>
                    <th className="px-2 py-1.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {cr.items.map((i) => (
                    <tr key={i.id} className="border-b border-border/30 last:border-0">
                      <td className="px-2 py-1.5 font-mono">{i.sku}</td>
                      <td className="px-2 py-1.5">{i.name}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums">{i.quantity}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                        {fmt.money(i.unitPrice, cr.refundCurrency ?? cr.salesOrder.currency)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                        {fmt.money(
                          i.quantity * i.unitPrice,
                          cr.refundCurrency ?? cr.salesOrder.currency,
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TabsContent>

          <TabsContent value="activity" className="pt-4">
            <ol className="space-y-2.5">
              {cr.statusHistory.map((h) => (
                <li
                  key={h.id}
                  className="flex gap-3 rounded border border-border/40 bg-muted/10 p-2.5 text-[12px]"
                >
                  <div className="w-1 self-stretch rounded bg-primary/30" />
                  <div className="flex-1 space-y-0.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">
                        {h.fromStatus
                          ? `${h.fromStatus} → ${h.toStatus}`
                          : h.toStatus}
                      </span>
                      <span className="font-mono text-[10.5px] tabular-nums text-muted-foreground">
                        {fmt.dateTime(h.occurredAt)}
                      </span>
                    </div>
                    {h.description && (
                      <div className="text-[11.5px] text-muted-foreground">{h.description}</div>
                    )}
                    {h.actorType && (
                      <div className="text-[10.5px] text-muted-foreground/70">
                        actor: {h.actorType}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 text-[13px]">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div>{children}</div>
    </div>
  );
}
